import React, { createContext, useRef, useState, useEffect, useMemo } from 'react';
import WebSocket from 'app/socket/WebSocket';
import CoreHttpHandler from 'http/services/CoreHttpHandler';
import { EventEmitter } from '../../../../events';

export const CallState = createContext(null);

const CALL_ICE_SERVERS = [
	{ urls: 'stun:stun.relay.metered.ca:80' },
	{
		urls: 'turn:standard.relay.metered.ca:80',
		username: '68414a16345cf8f36093d04d',
		credential: 'mW56UQboepKVCQrJ'
	},
	{
		urls: 'turn:standard.relay.metered.ca:80?transport=tcp',
		username: '68414a16345cf8f36093d04d',
		credential: 'mW56UQboepKVCQrJ'
	},
	{
		urls: 'turn:standard.relay.metered.ca:443',
		username: '68414a16345cf8f36093d04d',
		credential: 'mW56UQboepKVCQrJ'
	},
	{
		urls: 'turns:standard.relay.metered.ca:443?transport=tcp',
		username: '68414a16345cf8f36093d04d',
		credential: 'mW56UQboepKVCQrJ'
	}
];

// How long to wait for the other agent to accept or decline before giving the
// card its call back.
const TRANSFER_RESPONSE_TIMEOUT_MS = 45000;

// A ringing call auto-rejects after 60s (see the countdown effect), so a call
// that arrived while the agent was offline is only worth showing if it is still
// inside that window - otherwise going online after a long break would dump a
// pile of cards for calls that rang out long ago.
const RING_WINDOW_MS = 60000;

function CallContext({ children }) {
	// =================== States ===================
	// Lazy initialiser, so it is read once on mount rather than re-parsed on
	// every render, and guarded: JSON.parse on a value that is not valid JSON
	// (left by an older build, or hand-edited) throws, and a throw in a state
	// initialiser takes this whole provider - and every call feature with it -
	// down on every render.
	const [enableCallRingtone, setEnableCallRingtone] = useState(() => {
		try {
			const stored = localStorage.getItem('EnableCallTone');
			return stored === null ? false : JSON.parse(stored) === true;
		} catch (err) {
			return false;
		}
	});
	const [selectedRecipient, setselectedRecipient] = useState(null);
	const [openSessionModal, setOpenSessionModal] = useState(false);
	const [openn, setOpenn] = useState(false);
	const [isRingtonePlaying, setIsRingtonePlaying] = useState(false);
	const [incomingCalls, setIncomingCalls] = useState([]);
	const [activeCallId, setActiveCallId] = useState(null);
	const [hiddenCalls, setHiddenCalls] = useState([]);
	const [callTimers, setCallTimers] = useState({});
	const [activeReplyCallId, setActiveReplyCallId] = useState(null);
	const [showHiddenDropdown, setShowHiddenDropdown] = useState(false);
	const [cardOffsets, setCardOffsets] = useState({});
	const [cardPositions, setCardPositions] = useState({});
	const [snackbar, setSnackbar] = useState({
		message: '',
		color: 'success',
		trigger: 0
	});

	// =================== Call Transfer States ===================
	// Transfers offered to us by another agent, awaiting accept/reject.
	const [incomingTransfers, setIncomingTransfers] = useState([]);
	// The call we have offered to someone else and are waiting on. Kept in a
	// ref as well because the socket handlers below are registered once and
	// would otherwise close over a stale value.
	const [transferPendingCallId, setTransferPendingCallId] = useState(null);
	const transferPendingCallIdRef = useRef(null);
	// Transfer and conference ride the same transfer-call-request / accept
	// exchange; what differs is whether we drop our own leg once the other
	// agent is in. Remember which one we asked for so the accept knows.
	const transferModeRef = useRef('transfer');
	// Calls we pulled another agent into and are still on ourselves. Ending
	// such a call would hang up on everyone, so the card offers Leave instead.
	const [conferenceCallIds, setConferenceCallIds] = useState([]);
	// [{ userId, callId }] for every agent currently on a call, pushed by the
	// server on each join/leave. Used to keep agents who cannot take a call out
	// of the transfer and conference pickers.
	const [agentsOnCall, setAgentsOnCall] = useState([]);
	// What the card shows while a request is in flight and just after it
	// resolves: { [callId]: { mode, state, message } } where state is
	// 'pending' | 'accepted' | 'rejected' | 'failed'.
	const [transferStatus, setTransferStatus] = useState({});
	const transferStatusTimers = useRef({});
	// Guards against a request that is never answered leaving the card stuck
	// on "Transferring...".
	const transferResponseTimers = useRef({});
	// The agent list is the same for every picker and every invite that needs a
	// name resolved, so it is fetched once and shared instead of re-requested
	// on each dialog open.
	const [agentDirectory, setAgentDirectory] = useState([]);
	const agentDirectoryStateRef = useRef('idle'); // idle | loading | loaded

	// =================== Outgoing Call States ===================
	const [outgoingCalls, setOutgoingCalls] = useState([]);
	const [outgoingCardPositions, setOutgoingCardPositions] = useState({});
	const [activeOutgoingCallId, setActiveOutgoingCallId] = useState(null);

	// =================== Context & Redux ===================
	const socket = WebSocket.getSocket();

	// =================== Refs ===================
	const timerRefs = useRef({});
	const pcRefs = useRef({});
	const ringtoneRef = useRef(null);
	const dropdownRef = useRef(null);
	const quickRepliesRef = useRef(null);
	const callRefs = useRef({});
	const audioElementsRef = useRef({});
	const outgoingPcRefs = useRef({});
	const outgoingStreamRefs = useRef({});
	const outgoingTimerRefs = useRef({});
	// Remote ICE candidates that arrived before the answer did, keyed by callId.
	// addIceCandidate() is only legal once a remote description is set: Chrome
	// quietly buffers early candidates for us, Firefox rejects them with
	// InvalidStateError and the call then has nothing to build a candidate pair
	// from, so ICE ends in "failed". Hold them here and flush after the answer.
	const pendingRemoteCandidatesRef = useRef({});
	const mediaMonitorRefs = useRef({});
	// Legs still mid-handshake, so teardown can cancel one that is waiting on
	// the microphone prompt rather than leaving the mic open behind it.
	const establishingLegsRef = useRef({});
	// Calls that rang while the agent was offline. They are held rather than
	// discarded so going online can surface any that nobody has picked up.
	const offlineCallsRef = useRef([]);

	// =================== Recording Refs ===================
	const recorderRefs = useRef({});
	const recordedChunksRefs = useRef({});
	const localStreamRefs = useRef({});

	// =================== LocalStorage Data ===================
	let clientId = JSON.parse(localStorage.getItem('user_data'))?.client_id || null;
	let userId = JSON.parse(localStorage.getItem('user_data'))?.id || null;
	let agentPermissions = JSON.parse(localStorage.getItem('user_acl'));

	// Handing a live call to another agent is a separate privilege from being
	// able to take calls at all, so it gets its own sub-permission alongside
	// FRONT:/channels/whatsapp-call/recording. This gates *offering* a transfer
	// only - accepting one stays open to anyone who can take calls, otherwise a
	// transfer to them would be impossible to complete.
	// agentPermissions is re-parsed from localStorage on every render, so it is
	// a new object each time and useless as an effect dependency - an effect
	// keyed on it re-runs on every render, and this context re-renders once a
	// second while a call is up. Depend on this boolean instead.
	const canUseWhatsappCall = !!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call']);

	const canTransferCalls = !!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call/transfer']);

	// Pulling a second agent in and keeping the call yourself is a different
	// privilege from handing it over, so it gets its own key: an agent may be
	// allowed to escalate to a colleague without being allowed to offload.
	const canAddToConference = !!(
		agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call/conference']
	);

	const startRecording = async (callId, localStream, remoteStream) => {

		if (!localStream || !remoteStream) {
			console.error('Missing streams for recording');
			return;
		}

		// Guard against ontrack firing more than once and starting a second recorder
		if (recorderRefs.current[callId]) {
			return;
		}

		try {
			const AudioCtx = window.AudioContext || window['webkitAudioContext'];
			const audioContext = new AudioCtx();

			// Browsers often create the AudioContext in a "suspended" state. While
			// suspended no audio flows through the graph, so the MediaRecorder captures
			// nothing and produces zero chunks. Resume before wiring up the recorder.
			if (audioContext.state === 'suspended') {
				await audioContext.resume();
			}

			const destination = audioContext.createMediaStreamDestination();

			const localSource = audioContext.createMediaStreamSource(localStream);
			localSource.connect(destination);

			const remoteSource = audioContext.createMediaStreamSource(remoteStream);
			remoteSource.connect(destination);

			// Check supported mimeTypes
			let mimeType = 'audio/webm;codecs=opus';
			if (!MediaRecorder.isTypeSupported(mimeType)) {
				mimeType = 'audio/webm';
			}
			if (!MediaRecorder.isTypeSupported(mimeType)) {
				mimeType = 'audio/ogg';
			}
			if (!MediaRecorder.isTypeSupported(mimeType)) {
				mimeType = ''; // Let browser choose
			}

			const options = mimeType ? { mimeType } : {};
			const mediaRecorder = new MediaRecorder(destination.stream, options);

			recordedChunksRefs.current[callId] = [];

			mediaRecorder.ondataavailable = e => {
				if (e.data.size > 0) {
					recordedChunksRefs.current[callId].push(e.data);
				}
			};

			mediaRecorder.onstop = () => {
				const chunks = recordedChunksRefs.current[callId];

				if (chunks && chunks.length > 0) {
					const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
					uploadRecording(callId, blob);
				} else {
					console.error('No recorded chunks found');
				}
				delete recordedChunksRefs.current[callId];

				// Close the context only after the final data has been flushed,
				// otherwise tearing down the graph can drop the last chunk.
				if (audioContext.state !== 'closed') {
					audioContext.close();
				}
			};

			mediaRecorder.onerror = e => {
				console.error('MediaRecorder error:', e);
			};

			mediaRecorder.start(1000);

			recorderRefs.current[callId] = { mediaRecorder, audioContext };
		} catch (err) {
			console.error('Failed to start recording:', err);
		}
	};

	const stopRecording = callId => {
		const recorder = recorderRefs.current[callId];

		if (recorder) {

			if (recorder.mediaRecorder.state !== 'inactive') {
				// onstop flushes the final chunk, uploads, then closes the AudioContext.
				recorder.mediaRecorder.stop();
			} else if (recorder.audioContext.state !== 'closed') {
				recorder.audioContext.close();
			}
			delete recorderRefs.current[callId];
		} else {
			console.error('No recorder found for callId:', callId);
		}
	};

	const uploadRecording = (callId, blob) => {
		if (blob.size === 0) {
			console.error('Blob is empty, skipping upload');
			return;
		}

		const filename = `call_${callId}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.webm`;
		const audioFile = new File([blob], filename, { type: blob.type || 'audio/webm' });

		const formData = new FormData();
		formData.append('file', audioFile);
		formData.append('template_upload', 'false');

		CoreHttpHandler.request(
			'content',
			'upload',
			{ params: formData },
			response => {
				const uploadedUrl = response?.data?.data?.link;

				if (uploadedUrl) {
					insertCallRecording(callId, uploadedUrl);
				}
			},
			error => {
				console.error('Failed to upload recording:', error);
			}
		);
	};

	const insertCallRecording = (callId, recordingUrl) => {
		const params = {
			callId: callId,
			client_id: clientId,
			recordingUrl: recordingUrl
		};

		CoreHttpHandler.request(
			'conversations',
			'call_recording',
			params,
			response => {
				setSnackbar(prev => ({
					...prev,
					message: 'Call Ended Successfully',
					color: 'success',
					trigger: prev.trigger + 1
				}));
			},
			error => {
			}
		);
	};

	// =================== Handlers ===================
	// Toggle from the previous value rather than the captured one, and let the
	// effect below do the writing: storing and setting as two separate steps
	// off the same closure is how the stored value and the state end up
	// disagreeing, which reads as "the setting did not save".
	const handleCallRingtone = () => setEnableCallRingtone(prev => !prev);

	// Single writer for the persisted flag, so what is stored always matches
	// what is on screen - including the first mount, which rewrites the key if
	// it was missing or unreadable.
	useEffect(() => {
		try {
			localStorage.setItem('EnableCallTone', JSON.stringify(enableCallRingtone));
		} catch (err) {
			// Storage full or blocked - the toggle still works for this session.
		}
	}, [enableCallRingtone]);

	const toggleMinimizeAll = () => {
		if (hiddenCalls.length === incomingCalls.length - (activeCallId ? 1 : 0)) {
			setHiddenCalls([]);
		} else {
			setHiddenCalls(incomingCalls.filter(c => c.id !== activeCallId).map(c => c.id));
		}
	};

	const toggleMute = callId => {
		// Check if it's an incoming call
		const incomingCall = incomingCalls.find(c => c.id === callId);
		if (incomingCall) {
			setIncomingCalls(prev => prev.map(c => (c.id === callId ? { ...c, isMuted: !c.isMuted } : c)));
			const pc = pcRefs.current[callId];
			if (pc) {
				pc.getSenders().forEach(sender => {
					if (sender.track && sender.track.kind === 'audio') {
						sender.track.enabled = !sender.track.enabled;
					}
				});
			}
		}

		// Check if it's an outgoing call
		const outgoingCall = outgoingCalls.find(c => c.id === callId);
		if (outgoingCall) {
			setOutgoingCalls(prev => prev.map(c => (c.id === callId ? { ...c, isMuted: !c.isMuted } : c)));
			const stream = outgoingStreamRefs.current[callId];
			if (stream) {
				const audioTracks = stream.getAudioTracks();
				audioTracks.forEach(track => {
					track.enabled = !track.enabled;
				});
			}
		}
	};

	const hideCall = id => setHiddenCalls(prev => [...prev, id]);
	const restoreCalls = () => setHiddenCalls([]);
	const toggleReplies = (callId, event) => {
		if (event) {
			event.stopPropagation();
			event.preventDefault();
		}
		setActiveReplyCallId(prev => (prev === callId ? null : callId));
	};

	const handleDragEnd = (event, info, callId) => {
		// Check if it's an incoming call
		if (incomingCalls.find(c => c.id === callId)) {
			setCardPositions(prev => ({
				...prev,
				[callId]: {
					x: prev[callId].x + info.offset.x,
					y: prev[callId].y + info.offset.y
				}
			}));
		}

		// Check if it's an outgoing call
		if (outgoingCalls.find(c => c.id === callId)) {
			setOutgoingCardPositions(prev => ({
				...prev,
				[callId]: {
					x: prev[callId].x + info.offset.x,
					y: prev[callId].y + info.offset.y
				}
			}));
		}
	};

	const handleRestoreCall = id => {
		setHiddenCalls(prev => prev.filter(cid => cid !== id));
		setCallTimers(prev => ({
			...prev,
			[id]: 60
		}));
	};

	const sendQuickReplyMessage = (call, replyText) => {
		let params = {
			type: 'text',
			text: {
				to: [call.number],
				message: { text: replyText }
			}
		};
		CoreHttpHandler.request(
			'conversations',
			'send_text',
			params,
			response => {
				// Extract message ID from response
				const messageId = response?.data?.data?.message?.messages?.[0]?.id;
				if (messageId) {
					// Emit socket event with call ID instead of user ID
					socket.emit('call-message', {
						clientId: clientId,
						callId: call.id,
						messageId: messageId
					});
				}

				terminateCall(call.id);
				setSnackbar({
					message: 'Sent Successfully!',
					color: 'success',
					trigger: snackbar.trigger + 1
				});
			},
			error => console.log(error)
		);
	};

	const handleShowSnackbar = (message, color) => {
		setSnackbar(prev => ({
			...prev,
			message,
			color,
			trigger: prev.trigger + 1
		}));
	};

	const openCustomMessageBox = call => {
		setOpenSessionModal(true);
		setselectedRecipient(call);
	};

	const handleQuickReplySelect = (replyText, call) => {
		if (replyText === 'Custom message...') {
			openCustomMessageBox(call);
		} else {
			sendQuickReplyMessage(call, replyText);
		}
		setActiveReplyCallId(null);
		setShowHiddenDropdown(false);
	};

	// Helper function to stop ringtone properly
	const stopRingtone = () => {
		if (ringtoneRef.current) {
			ringtoneRef.current.pause();
			ringtoneRef.current.currentTime = 0;
		}
		setIsRingtonePlaying(false);
	};

	// Fetched on demand and kept: the picker opens instantly and identically
	// every time instead of showing a spinner on the first open and a stale
	// list on the next, and repeat opens cost no request.
	const loadAgentDirectory = () => {
		if (agentDirectoryStateRef.current !== 'idle') return;
		agentDirectoryStateRef.current = 'loading';

		CoreHttpHandler.request(
			'conversations',
			'agent_listNew',
			{ columns: 'USR.id, USR.username', role: 64, displayed: true, enabled: true },
			response => {
				agentDirectoryStateRef.current = 'loaded';
				setAgentDirectory(response?.data?.data?.agents?.data || []);
			},
			() => {
				// Back to idle so the next open retries rather than being stuck
				// with an empty list for the rest of the session.
				agentDirectoryStateRef.current = 'idle';
			}
		);
	};

	// Asked for by the picker when it opens. Everything after that arrives on
	// the server's own agents-on-call-updated pushes, so there is no reason to
	// poll or to pull on a schedule.
	const refreshAgentsOnCall = () => {
		if (!socket || !canUseWhatsappCall) return;
		socket.emit('get-agents-on-call', { clientId });
	};

	const dropOfflineCall = callId => {
		offlineCallsRef.current = offlineCallsRef.current.filter(c => c.id !== callId);
	};

	// Called when the agent comes back online. Anything still inside the ring
	// window is shown with the time it has actually got left, not a fresh 60s,
	// so a call that has been ringing for 50s does not look brand new.
	const flushOfflineCalls = () => {
		const now = Date.now();
		const live = offlineCallsRef.current.filter(c => now - c.receivedAt < RING_WINDOW_MS);
		offlineCallsRef.current = [];

		if (!live.length) return;

		// Seed the countdown before the calls land: the effect that fills in
		// timers only defaults to 60 for ids it has not seen.
		setCallTimers(prev => {
			const updated = { ...prev };
			live.forEach(c => {
				if (!updated[c.id]) {
					updated[c.id] = Math.max(1, Math.ceil((RING_WINDOW_MS - (now - c.receivedAt)) / 1000));
				}
			});
			return updated;
		});

		setIncomingCalls(prev => {
			const known = new Set(prev.map(c => c.id));
			const additions = live
				.filter(c => !known.has(c.id))
				.map(c => ({ id: c.id, name: c.name, number: c.number, timer: 0 }));

			if (!additions.length) return prev;

			const updated = [...prev, ...additions];
			localStorage.setItem('incomingCalls', JSON.stringify(updated));
			return updated;
		});

		loadAgentDirectory();
	};

	const clearTransferResponseTimer = callId => {
		if (transferResponseTimers.current[callId]) {
			clearTimeout(transferResponseTimers.current[callId]);
			delete transferResponseTimers.current[callId];
		}
	};

	// Shows on the call card, then clears itself so a finished transfer does
	// not leave a stale banner behind.
	const setTransferState = (callId, next, holdMs = 0) => {
		if (transferStatusTimers.current[callId]) {
			clearTimeout(transferStatusTimers.current[callId]);
			delete transferStatusTimers.current[callId];
		}

		setTransferStatus(prev => {
			if (!next) {
				const copy = { ...prev };
				delete copy[callId];
				return copy;
			}
			return { ...prev, [callId]: next };
		});

		if (next && holdMs) {
			transferStatusTimers.current[callId] = setTimeout(() => {
				delete transferStatusTimers.current[callId];
				setTransferStatus(prev => {
					const copy = { ...prev };
					delete copy[callId];
					return copy;
				});
			}, holdMs);
		}
	};

	// Stopping the track is what actually turns the microphone off - dropping
	// the reference alone leaves the browser's recording indicator on and the
	// device held. Every teardown path goes through here.
	const releaseLocalStream = callId => {
		const stream = localStreamRefs.current[callId];
		if (!stream) return;

		stream.getTracks().forEach(track => track.stop());
		delete localStreamRefs.current[callId];
	};

	// Helper function to clean up audio elements for a specific call
	const cleanupCallAudio = callId => {
		if (audioElementsRef.current[callId]) {
			audioElementsRef.current[callId].pause();
			audioElementsRef.current[callId].remove();
			delete audioElementsRef.current[callId];
		}
	};

	// Chrome lets a WebRTC <audio> element autoplay on the strength of the
	// Accept click; Edge and Firefox frequently do not, because ontrack fires
	// long after that gesture has expired. A blocked play() is completely
	// silent - no error, no audio, nothing in the UI - so retry on the next
	// interaction anywhere in the page, the same way the ringtone is unlocked.
	const playRemoteAudio = audio => {
		audio.play().catch(err => {
			console.warn('Remote audio playback blocked, waiting for interaction:', err);

			const retry = () => {
				document.removeEventListener('click', retry);
				document.removeEventListener('keydown', retry);
				audio.play().catch(retryErr => console.warn('Remote audio still blocked:', retryErr));
			};

			document.addEventListener('click', retry);
			document.addEventListener('keydown', retry);
		});
	};

	// "The call connects but nobody hears anything" has four very different
	// causes that look identical from the UI: the transport never came up, our
	// mic audio is not leaving, the peer's audio is not arriving, or it is
	// arriving and the element decoding it is not playing. Log enough to tell
	// them apart - this runs in every browser, not just the broken one.
	const monitorCallMedia = (callId, pc) => {
		let ticks = 0;

		const interval = setInterval(async () => {
			if (pc.connectionState === 'closed' || pc.signalingState === 'closed') {
				clearInterval(interval);
				delete mediaMonitorRefs.current[callId];
				return;
			}

			let sentBytes = 0;
			let receivedBytes = 0;
			let transport = 'none';

			try {
				const stats = await pc.getStats(null);
				stats.forEach(report => {
					const kind = report.kind || report.mediaType;
					if (report.type === 'outbound-rtp' && kind === 'audio') {
						sentBytes = report.bytesSent || 0;
					} else if (report.type === 'inbound-rtp' && kind === 'audio') {
						receivedBytes = report.bytesReceived || 0;
					} else if (report.type === 'candidate-pair' && report.state === 'succeeded' && report.nominated) {
						transport = `${report.localCandidateId} -> ${report.remoteCandidateId}`;
					}
				});
			} catch (err) {
				clearInterval(interval);
				delete mediaMonitorRefs.current[callId];
				return;
			}

			// A negotiated direction of anything but sendrecv means one side of
			// the call was given away in the answer, which looks exactly like a
			// dead microphone from the UI.
			let directions = 'n/a';
			try {
				directions = pc
					.getTransceivers()
					.map(t => `${t.mid || '?'}=${t.currentDirection || t.direction}`)
					.join(' ');
			} catch (err) {
				// getTransceivers is not available everywhere; not worth failing over.
			}

			const audio = audioElementsRef.current[callId];
			console.info(
				`[call ${callId}] ice=${pc.iceConnectionState} dtls=${pc.connectionState}`,
				`| sent=${sentBytes}B received=${receivedBytes}B`,
				`| dir=${directions}`,
				`| pair=${transport}`,
				`| ${audio ? `audio paused=${audio.paused} muted=${audio.muted} volume=${audio.volume}` : 'no audio element'}`
			);

			ticks += 1;
			if (ticks >= 6) {
				clearInterval(interval);
				delete mediaMonitorRefs.current[callId];
			}
		}, 3000);

		mediaMonitorRefs.current[callId] = interval;
	};

	// Joining a call is the same WebRTC handshake whether we are answering a
	// fresh one or picking up a leg transferred from another agent: our own
	// offer goes out and the bridge answers it. Only the event the offer
	// travels on differs, so that is what the caller supplies.
	const establishCallLeg = async (callId, sendOffer) => {
		// Teardown flips this if the call goes away while we are still awaiting
		// the mic or building the offer, so the awaits below know to clean up
		// after themselves instead of handing back a live microphone.
		const leg = { cancelled: false };
		establishingLegsRef.current[callId] = leg;

		const newPc = new RTCPeerConnection({ iceServers: CALL_ICE_SERVERS });

		newPc.ontrack = e => {
			// e.streams is only populated when the answer carries an a=msid
			// for the track. Chrome and Edge invent a stream when it does
			// not; Firefox hands back an empty list, and the element then
			// gets srcObject = undefined and plays nothing while the call
			// looks perfectly connected. Build the stream from the track.
			const remoteStream = e.streams[0] || new MediaStream([e.track]);
			cleanupCallAudio(callId);

			const audio = document.createElement('audio');
			audio.srcObject = remoteStream;
			audio.autoplay = true;
			audio.playsInline = true;
			document.body.appendChild(audio);
			audioElementsRef.current[callId] = audio;
			playRemoteAudio(audio);

			setIncomingCalls(prev => prev.map(c => (c.id === callId ? { ...c, stream: remoteStream } : c)));

			// Start recording when we have both streams
			const localStream = localStreamRefs.current[callId];
			if (localStream && remoteStream) {
				startRecording(callId, localStream, remoteStream);
			}
		};

		newPc.onicecandidate = event => {
			if (event.candidate) {
				socket.emit('browser-candidate', { callId, candidate: event.candidate });
			}
		};

		// remove countdown timer when accepted
		setCallTimers(prev => {
			const copy = { ...prev };
			delete copy[callId];
			return copy;
		});

		// start duration timer
		startCallTimer(callId);
		setActiveCallId(callId);

		try {
			// 🎤 local mic
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

			// The call can end while the permission prompt is still up. Nothing
			// has this stream on record yet, so teardown had nothing to stop -
			// release it here or the microphone stays live with no call
			// attached to it.
			if (leg.cancelled) {
				stream.getTracks().forEach(track => track.stop());
				return false;
			}

			// Answering and then taking a transfer of the same call runs this
			// twice; without releasing the first stream its track keeps the mic
			// open, because only the ref we are about to overwrite is ever
			// stopped.
			releaseLocalStream(callId);
			localStreamRefs.current[callId] = stream;
			stream.getTracks().forEach(track => newPc.addTrack(track, stream));

			// Which device the browser actually picked, and whether it is
			// producing anything. `muted` here is the browser telling us the
			// source is delivering silence - a different thing from the
			// in-call mute button, and the one that explains a call the peer
			// cannot hear.
			stream.getAudioTracks().forEach(track => {
				console.info(
					`[call ${callId}] mic "${track.label}"`,
					`enabled=${track.enabled} muted=${track.muted} state=${track.readyState}`
				);
			});

			const offer = await newPc.createOffer();
			await newPc.setLocalDescription(offer);

			// Same again: the leg can die between the mic and the offer, and
			// the peer connection is not registered yet, so teardown cannot
			// reach either of them.
			if (leg.cancelled) {
				releaseLocalStream(callId);
				newPc.close();
				return false;
			}

			localStorage.setItem('activeCallId', callId);

			// Register before the offer goes out, not after: the answer and
			// the first remote candidates can come back before this line
			// would otherwise have run, and both handlers drop anything they
			// cannot find a peer connection for.
			pcRefs.current[callId] = newPc;
			monitorCallMedia(callId, newPc);
			delete establishingLegsRef.current[callId];

			sendOffer(offer.sdp);
			return true;
		} catch (e) {
			setSnackbar({
				message: 'Please enable the microphone',
				color: 'error',
				trigger: snackbar.trigger + 1
			});
			return false;
		}
	};

	const stopMonitoringCallMedia = callId => {
		if (mediaMonitorRefs.current[callId]) {
			clearInterval(mediaMonitorRefs.current[callId]);
			delete mediaMonitorRefs.current[callId];
		}
	};

	// =================== Outgoing Call Functions ===================
	const initiateOutgoingCall = async (phoneNumber, recipient) => {
		// Check if there's already an active outgoing call
		const existingActiveOutgoing = localStorage.getItem('activeOutgoingCallId');
		if (existingActiveOutgoing) {
			handleShowSnackbar('Another outgoing call is already active', 'warning');
			return false;
		}

		// Create PeerConnection
		const newPc = new RTCPeerConnection({
			iceServers: [
				{ urls: 'stun:stun.relay.metered.ca:80' },
				{
					urls: 'turn:standard.relay.metered.ca:80',
					username: '68414a16345cf8f36093d04d',
					credential: 'mW56UQboepKVCQrJ'
				},
				{
					urls: 'turn:standard.relay.metered.ca:80?transport=tcp',
					username: '68414a16345cf8f36093d04d',
					credential: 'mW56UQboepKVCQrJ'
				},
				{
					urls: 'turn:standard.relay.metered.ca:443',
					username: '68414a16345cf8f36093d04d',
					credential: 'mW56UQboepKVCQrJ'
				},
				{
					urls: 'turns:standard.relay.metered.ca:443?transport=tcp',
					username: '68414a16345cf8f36093d04d',
					credential: 'mW56UQboepKVCQrJ'
				}
			]
		});

		// Handle incoming audio
		newPc.ontrack = e => {
			const audio = document.createElement('audio');
			audio.srcObject = e.streams[0];
			audio.autoplay = true;
			audio.controls = true;
			document.body.appendChild(audio);
		};

		try {
			// Get microphone audio and store the stream
			const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
			stream.getTracks().forEach(track => newPc.addTrack(track, stream));

			// Create offer
			const offer = await newPc.createOffer();
			await newPc.setLocalDescription(offer);

			const sdp = offer.sdp;

			// Send offer to backend
			socket.emit('initiate-call', {
				userId: userId,
				clientId: clientId,
				number: phoneNumber,
				sdp: sdp
			});

			// Set up temporary socket listeners
			const handleInitiatedResponse = ({ callId, number, clientId, userId }) => {
				// Create outgoing call object
				const outgoingCall = {
					id: callId,
					name: recipient?.name || 'Unknown',
					number: phoneNumber,
					status: 'CALLING',
					timer: 0,
					isMuted: false,
					type: 'outgoing'
				};

				// Store references
				outgoingPcRefs.current[callId] = newPc;
				outgoingStreamRefs.current[callId] = stream;
				localStreamRefs.current[callId] = stream;

				setOutgoingCalls(prev => [...prev, outgoingCall]);
				socket.off('initiated-call-response', handleInitiatedResponse);
			};

			socket.on('initiated-call-response', handleInitiatedResponse);
			return true;
		} catch (error) {
			console.error('Error accessing microphone:', error);
			handleShowSnackbar('Please enable microphone access', 'error');
			return false;
		}
	};

	const terminateOutgoingCall = callId => {
		stopRecording(callId);

		socket.emit('terminate-call', { clientId: clientId, userId: userId, callId: callId });

		if (outgoingStreamRefs.current[callId]) {
			outgoingStreamRefs.current[callId].getTracks().forEach(track => {
				track.stop();
			});
			delete outgoingStreamRefs.current[callId];
		}

		if (localStreamRefs.current[callId]) {
			delete localStreamRefs.current[callId];
		}

		if (outgoingPcRefs.current[callId]) {
			outgoingPcRefs.current[callId].close();
			delete outgoingPcRefs.current[callId];
		}

		// Stop timer
		stopOutgoingCallTimer(callId);

		// Remove from outgoing calls
		setOutgoingCalls(prev => prev.filter(c => c.id !== callId));

		// Reset active outgoing call if this was active
		if (activeOutgoingCallId === callId) {
			setActiveOutgoingCallId(null);
			localStorage.removeItem('activeOutgoingCallId');
		}
	};

	const startOutgoingCallTimer = callId => {
		if (outgoingTimerRefs.current[callId]) {
			clearInterval(outgoingTimerRefs.current[callId]);
		}

		setOutgoingCalls(prev => prev.map(call => (call.id === callId ? { ...call, timer: 0 } : call)));

		outgoingTimerRefs.current[callId] = setInterval(() => {
			setOutgoingCalls(prev =>
				prev.map(call => (call.id === callId ? { ...call, timer: (call.timer || 0) + 1 } : call))
			);
		}, 1000);
	};

	const stopOutgoingCallTimer = callId => {
		if (outgoingTimerRefs.current[callId]) {
			clearInterval(outgoingTimerRefs.current[callId]);
			delete outgoingTimerRefs.current[callId];
		}
	};

	// =================== Effects ===================
	useEffect(() => {
		// const audio = new Audio('https://upload.its.com.pk/3011937d-4a5e-4af8-9739-8865b976e7e4.mp3');
		const audio = new Audio('/assets/sounds/ring-notification.mp3');
		audio.loop = true;
		audio.muted = true;
		ringtoneRef.current = audio;

		const unlockAudio = () => {
			audio.muted = false;
			document.removeEventListener('click', unlockAudio);
			document.removeEventListener('keydown', unlockAudio);
		};

		document.addEventListener('click', unlockAudio);
		document.addEventListener('keydown', unlockAudio);

		return () => {
			document.removeEventListener('click', unlockAudio);
			document.removeEventListener('keydown', unlockAudio);
		};
	}, []);

	// ENHANCED: Ringtone control logic - mute when outgoing call is active
	useEffect(() => {
		const ringtone = ringtoneRef.current;
		const hasUnansweredCalls =
			incomingCalls.some(c => c.id !== activeCallId) || incomingTransfers.length > 0;
		const hasActiveOutgoingCall = activeOutgoingCallId !== null;
		const shouldPlayRingtone = enableCallRingtone && hasUnansweredCalls && !activeCallId && !hasActiveOutgoingCall;

		if (shouldPlayRingtone) {
			if (ringtone.paused) {
				ringtone.loop = true;
				ringtone.play().catch(err => console.warn('Ringtone play blocked:', err));
				setIsRingtonePlaying(true);
			}
		} else {
			stopRingtone();
		}
	}, [incomingCalls, incomingTransfers, activeCallId, enableCallRingtone, activeOutgoingCallId]);

	// Socket listeners for outgoing calls
	useEffect(() => {
		if (!socket) return;

		const handleWhatsAppAnswer = ({ callId, sdp }) => {
			const pc = outgoingPcRefs.current[callId];
			if (!pc) {
				console.error('No PeerConnection found for outgoing call:', callId);
				return;
			}
			try {
				pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }));

				pc.ontrack = e => {
					// See the incoming-call handler: Firefox leaves e.streams
					// empty when the answer has no a=msid for the track.
					const remoteStream = e.streams[0] || new MediaStream([e.track]);
					const localStream = localStreamRefs.current[callId];

					// Clean up existing audio element
					if (audioElementsRef.current[callId]) {
						audioElementsRef.current[callId].pause();
						audioElementsRef.current[callId].remove();
					}

					const audio = document.createElement('audio');
					audio.srcObject = remoteStream;
					audio.autoplay = true;
					document.body.appendChild(audio);
					audioElementsRef.current[callId] = audio;
					playRemoteAudio(audio);

					// Start recording for outgoing call
					if (localStream && remoteStream) {
						startRecording(callId, localStream, remoteStream);
					}
				};
			} catch (err) {
				console.error('Failed to set remote description for outgoing call:', err);
			}
		};

		const handleCallStatusUpdate = ({ callId, callStatus }) => {
			setOutgoingCalls(prev =>
				prev.map(call => {
					if (call.id === callId) {
						const updatedCall = { ...call, status: callStatus };

						// If call is accepted, set as active and start timer
						if (callStatus === 'ACCEPTED') {
							setActiveOutgoingCallId(callId);
							localStorage.setItem('activeOutgoingCallId', callId);
							startOutgoingCallTimer(callId);

							// Stop ringtone when outgoing call is accepted
							stopRingtone();
						}

						return updatedCall;
					}
					return call;
				})
			);
		};

		const handleOutgoingCallEnded = ({ callId }) => {
			stopRecording(callId);

			setOutgoingCalls(prev => prev.filter(c => c.id !== callId));

			if (audioElementsRef.current[callId]) {
				audioElementsRef.current[callId].pause();
				audioElementsRef.current[callId].remove();
				delete audioElementsRef.current[callId];
			}

			if (outgoingStreamRefs.current[callId]) {
				outgoingStreamRefs.current[callId].getTracks().forEach(track => track.stop());
				delete outgoingStreamRefs.current[callId];
			}

			if (localStreamRefs.current[callId]) {
				delete localStreamRefs.current[callId];
			}

			if (outgoingPcRefs.current[callId]) {
				outgoingPcRefs.current[callId].close();
				delete outgoingPcRefs.current[callId];
			}

			stopOutgoingCallTimer(callId);

			if (activeOutgoingCallId === callId) {
				setActiveOutgoingCallId(null);
				localStorage.removeItem('activeOutgoingCallId');
			}
		};

		socket.on('whatsapp-answer', handleWhatsAppAnswer);
		socket.on('call-status-update', handleCallStatusUpdate);
		socket.on('call-ended', handleOutgoingCallEnded);

		return () => {
			socket.off('whatsapp-answer', handleWhatsAppAnswer);
			socket.off('call-status-update', handleCallStatusUpdate);
			socket.off('call-ended', handleOutgoingCallEnded);
		};
	}, [socket, activeOutgoingCallId]);

	useEffect(() => {
		setOutgoingCardPositions(prev => {
			const slotHeight = 136;
			const existingIds = outgoingCalls.map(c => c.id);
			let updated = Object.fromEntries(Object.entries(prev).filter(([id]) => existingIds.includes(id)));
			let currentY = 0;
			outgoingCalls.forEach(call => {
				if (!updated[call.id]) {
					updated[call.id] = { x: 0, y: currentY };
					currentY += slotHeight;
				}
			});
			return updated;
		});
	}, [outgoingCalls]);

	useEffect(() => {
		localStorage.removeItem('activeCallId');
		localStorage.removeItem('activeOutgoingCallId');
		setActiveCallId(null);
		setActiveOutgoingCallId(null);
	}, []);

	useEffect(() => {
		if (incomingCalls.length === 0 && activeCallId) {
			setActiveCallId(null);
			localStorage.removeItem('activeCallId');
			stopRingtone();
		}
	}, [incomingCalls, activeCallId]);

	useEffect(() => {
		const handleClickOutside = event => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setShowHiddenDropdown(false);
			}
		};
		if (showHiddenDropdown) {
			document.addEventListener('mousedown', handleClickOutside);
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
		}
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [showHiddenDropdown]);

	useEffect(() => {
		setCardPositions(prev => {
			const slotHeight = 136;
			const existingIds = incomingCalls.map(c => c.id);
			let updated = Object.fromEntries(Object.entries(prev).filter(([id]) => existingIds.includes(id)));
			const isDraggedCall = pos => pos.x !== 0 || pos.y % slotHeight !== 0;
			const sortedCalls = incomingCalls
				.filter(c => updated[c.id])
				.sort((a, b) => updated[a.id].y - updated[b.id].y);
			let currentY = 0;
			sortedCalls.forEach(call => {
				const pos = updated[call.id];
				if (!isDraggedCall(pos)) {
					if (pos.y !== currentY) {
						pos.y = currentY;
						pos.x = 0;
					}
				}
				currentY = Math.max(currentY + slotHeight, pos.y + slotHeight);
			});
			incomingCalls.forEach(call => {
				if (!updated[call.id]) {
					updated[call.id] = { x: 0, y: currentY };
					currentY += slotHeight;
				}
			});
			return updated;
		});
	}, [incomingCalls]);

	useEffect(() => {
		setCallTimers(prev => {
			const updated = { ...prev };
			incomingCalls.forEach(call => {
				if (!updated[call.id]) {
					updated[call.id] = 60;
				}
			});
			return updated;
		});
	}, [incomingCalls]);

	useEffect(() => {
		const interval = setInterval(() => {
			setCallTimers(prev => {
				const updated = {};
				Object.entries(prev).forEach(([id, time]) => {
					if (activeCallId === id) return;
					if (time > 1) {
						updated[id] = time - 1;
					} else {
						respondCall('reject', id);
						setHiddenCalls(h => h.filter(cid => cid !== id));
						setCardPositions(p => {
							const copy = { ...p };
							delete copy[id];
							return copy;
						});
						setIncomingCalls(prev => prev.filter(c => c.id !== id));
					}
				});
				return updated;
			});
		}, 1000);
		return () => clearInterval(interval);
	}, [activeCallId]);

	useEffect(() => {
		const handleClickOutside = event => {
			const isMessageButton = event.target.closest('[data-message-button]');
			if (quickRepliesRef.current && !quickRepliesRef.current.contains(event.target) && !isMessageButton) {
				setActiveReplyCallId(null);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// The permission check used to wrap the useEffect call itself. agentPermissions
	// is re-read from localStorage on every render, so an ACL change mid-session
	// (re-login, impersonation) altered the number of hooks between renders and
	// React threw "Rendered fewer hooks than expected", taking the app down. The
	// hooks are unconditional now; the check moved inside the callback.
	useEffect(() => {
		if (!socket) return undefined;
		if (!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call'])) {
			return undefined;
		}
		const handleCallIsComing = ({ callId, callerName, callerNumber }) => {
			let online = JSON.parse(localStorage.getItem('online'));

			// Offline agents get no card, but the call is remembered instead of
			// thrown away: if it is still ringing when they come back online it
			// is shown then. remove-call and call-ended both clear it, so a call
			// somebody else answered never resurfaces.
			if (online == false) {
				if (!offlineCallsRef.current.some(c => c.id === callId)) {
					offlineCallsRef.current.push({
						id: callId,
						name: callerName,
						number: callerNumber,
						receivedAt: Date.now()
					});
				}
				return;
			}

			setIncomingCalls(prev => {
				const updated = [...prev, { id: callId, name: callerName, number: callerNumber, timer: 0 }];
				localStorage.setItem('incomingCalls', JSON.stringify(updated));
				return updated;
			});

			// Warm the directory now rather than when the transfer dialog is
			// opened mid-call: it is cached, so this costs one request, and it
			// means agent names are ready for the picker and for turning the
			// ids in the agents-on-call feed into names.
			loadAgentDirectory();
		};
		socket.on('call-is-coming', handleCallIsComing);
		return () => socket.off('call-is-coming', handleCallIsComing);
	}, [socket, agentPermissions]);

	useEffect(() => {
		if (!socket) return undefined;
		if (!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call'])) {
			return undefined;
		}

		const addRemoteCandidate = async (pc, candidate) => {
			try {
				await pc.addIceCandidate(new RTCIceCandidate(candidate));
			} catch (err) {
				console.error('Failed to add ICE candidate:', err);
			}
		};

		const handleBrowserAnswer = async ({ callId, sdp }) => {
			const pc = pcRefs.current[callId];
			if (!pc) return;
			try {
				await pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }));
			} catch (err) {
				console.error('Failed to set remote description:', err);
				return;
			}

			// Anything that arrived while the answer was still in flight is
			// only addable now. Take the queue away first so candidates that
			// land during the flush go straight through instead of piling up
			// in a buffer nothing drains.
			const pending = pendingRemoteCandidatesRef.current[callId] || [];
			delete pendingRemoteCandidatesRef.current[callId];
			await Promise.all(pending.map(candidate => addRemoteCandidate(pc, candidate)));
		};

		const handleBrowserCandidate = async ({ callId, candidate }) => {
			const pc = pcRefs.current[callId];
			if (!pc) return;

			if (!pc.remoteDescription || !pc.remoteDescription.type) {
				const queue = pendingRemoteCandidatesRef.current[callId] || [];
				queue.push(candidate);
				pendingRemoteCandidatesRef.current[callId] = queue;
				return;
			}

			await addRemoteCandidate(pc, candidate);
		};

		// Same cleanup as hanging up ourselves, minus telling the server -
		// it is the one that just told us. Sharing teardownCallLeg keeps
		// the microphone release in one place instead of two copies that
		// drift apart.
		const handleCallEnded = ({ callId }) => teardownCallLeg(callId);

		const handleStartBrowserTimer = ({ callId }) => startCallTimer(callId);

		socket.on('browser-answer', handleBrowserAnswer);
		socket.on('browser-candidate', handleBrowserCandidate);
		socket.on('call-ended', handleCallEnded);
		socket.on('start-browser-timer', handleStartBrowserTimer);

		return () => {
			socket.off('browser-answer', handleBrowserAnswer);
			socket.off('browser-candidate', handleBrowserCandidate);
			socket.off('call-ended', handleCallEnded);
			socket.off('start-browser-timer', handleStartBrowserTimer);
		};
	}, [socket, activeCallId, agentPermissions]);

	useEffect(() => {
		if (!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call'])) {
			return;
		}
		const storedCalls = localStorage.getItem('incomingCalls');
		if (storedCalls) setIncomingCalls(JSON.parse(storedCalls));
		const storedActiveCallId = localStorage.getItem('activeCallId');
		if (storedActiveCallId) setActiveCallId(storedActiveCallId);
		const storedActiveOutgoingCallId = localStorage.getItem('activeOutgoingCallId');
		if (storedActiveOutgoingCallId) setActiveOutgoingCallId(storedActiveOutgoingCallId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!socket) return undefined;
		if (!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call'])) {
			return undefined;
		}
		let userID = localStorage.getItem('user_data') ? JSON.parse(localStorage.getItem('user_data')).id : null;
		const handleRemoveCall = ({ callId, userId }) => {
			// Somebody took it. Drop it from the offline buffer either way, so
			// coming back online does not resurrect an answered call.
			dropOfflineCall(callId);

			if (userId === userID) return;

			// Someone else took this call over. Dropping the card is enough
			// while it was only being offered to us, but if we were actually on
			// it our leg has to go too - otherwise the microphone stays live
			// and we sit in a call the server no longer counts us in.
			if (pcRefs.current[callId] || localStreamRefs.current[callId]) {
				console.info(`[call ${callId}] removed by another agent - tearing down our leg`);
				teardownCallLeg(callId);
				return;
			}

			setIncomingCalls(prev => prev.filter(c => c.id !== callId));
		};
		socket.on('remove-call', handleRemoveCall);
		return () => socket.off('remove-call', handleRemoveCall);
	}, [socket, agentPermissions]);

	// =================== Coming back online ===================
	// The header toggle dispatches this. Deliberately never unsubscribed:
	// EventEmitter.unsubscribe() drops every handler for the event, so calling
	// it here would silently kill the 'Online' listeners in the chat apps too.
	// This provider mounts once for the session, and flushOfflineCalls empties
	// the buffer as it runs, so a repeat call is a no-op.
	useEffect(() => {
		EventEmitter.subscribe('Online', isOnline => {
			if (isOnline) flushOfflineCalls();
		});
	}, []);

	// =================== Agents-on-call tracking ===================
	// Only the listener lives here. The pull is deliberately NOT fired from
	// this effect: canUseWhatsappCall keeps it from re-running on every render,
	// but the list is only ever read by the transfer/conference picker, so the
	// picker asks for it when it opens (refreshAgentsOnCall) and the server's
	// own pushes keep it current from then on.
	useEffect(() => {
		if (!socket || !canUseWhatsappCall) return undefined;

		const handleAgentsOnCall = payload => {
			setAgentsOnCall(Array.isArray(payload?.agentsOnCall) ? payload.agentsOnCall : []);
		};

		socket.on('agents-on-call-updated', handleAgentsOnCall);
		return () => socket.off('agents-on-call-updated', handleAgentsOnCall);
	}, [socket, canUseWhatsappCall]);

	// =================== Call Transfer Socket Listeners ===================
	useEffect(() => {
		if (!socket) return undefined;
		if (!(agentPermissions && agentPermissions['FRONT:/channels/whatsapp-call'])) return undefined;

		// incoming-transfer and incoming-conference-invite carry the same shape;
		// the mode decides what the card says and, more importantly, which pair
		// of accept/reject events the answer has to go back out on.
		const handleInvite = mode => payload => {
			if (!payload || !payload.callId) return;
			const invite = { ...payload, mode };

			setIncomingTransfers(prev =>
				prev.some(t => t.callId === invite.callId) ? prev : [...prev, invite]
			);

			// The invite only carries fromUserId, and an id means nothing on a
			// card. The agent list we already use for the picker maps it to a
			// name, so resolve it against the shared directory rather than
			// firing another request per invite.
			if (!payload.fromUserId) return;
			loadAgentDirectory();
		};

		// Sent to the agent who accepted. It also reaches the requesting agent
		// in some cases, so both sides are handled here.
		const handleAccepted = mode => ({ callId }) => {
			console.info(
				`[${mode}] accepted ${callId}`,
				`pending=${transferPendingCallIdRef.current} mode=${transferModeRef.current}`
			);

			if (transferPendingCallIdRef.current === callId) {
				// Decide from what WE asked for, not from which event family
				// came back. A conference invite answered on the transfer
				// family - which happens whenever the server has not split the
				// events - would otherwise make the inviting agent leave the
				// call they deliberately stayed on.
				const intent = transferModeRef.current;

				markTransferPending(null);
				clearTransferResponseTimer(callId);

				// Staying is the whole point of a conference: the customer now
				// has both of us. A transfer means handing over and getting out
				// of the way.
				if (intent === 'conference') {
					setConferenceCallIds(prev => (prev.includes(callId) ? prev : [...prev, callId]));
					setTransferState(callId, { mode: intent, state: 'accepted', message: 'Agent joined' }, 4000);
					handleShowSnackbar('Agent added to the call', 'success');
					return;
				}

				// Show the outcome before the card goes, otherwise the call just
				// vanishes with no indication it was actually picked up.
				setTransferState(callId, { mode: intent, state: 'accepted', message: 'Transfer accepted' });
				handleShowSnackbar('Call transferred', 'success');
				setTimeout(() => leaveCall(callId), 1200);
				return;
			}

			// We are the agent who accepted - but only say so if we are really
			// on this call. A requesting agent that has already handed the call
			// over and left would otherwise be told it just joined one.
			if (pcRefs.current[callId]) {
				handleShowSnackbar('You are now connected to the call', 'success');
			}
		};

		const handleRejected = mode => ({ callId, rejectedBy }) => {
			// rejectedBy is a user id. Show a name or nothing - a bare id on
			// screen means nothing to an agent.
			const byName = agentDirectory.find(a => String(a.id) === String(rejectedBy))?.username;
			const what = mode === 'conference' ? 'Invite' : 'Transfer';

			if (transferPendingCallIdRef.current === callId) {
				markTransferPending(null);
				clearTransferResponseTimer(callId);
				// We kept the call, so say so - the agent needs to know they are
				// still the one on it.
				setTransferState(
					callId,
					{ mode, state: 'rejected', message: byName ? `Declined by ${byName}` : `${what} declined` },
					6000
				);
			}

			handleShowSnackbar(byName ? `${what} declined by ${byName}` : `${what} declined`, 'warning');
		};

		const handleFailed = mode => ({ callId, reason }) => {
			if (transferPendingCallIdRef.current === callId) {
				markTransferPending(null);
				clearTransferResponseTimer(callId);
				setTransferState(
					callId,
					{ mode, state: 'failed', message: reason || 'Request failed' },
					6000
				);
			}
			// The call is gone on the server, so clear any prompt still
			// offering it rather than leaving a card nobody can act on.
			setIncomingTransfers(prev => prev.filter(t => t.callId !== callId));
			handleShowSnackbar(
				reason || (mode === 'conference' ? 'Could not add agent' : 'Call transfer failed'),
				'error'
			);
		};

		const listeners = {
			'incoming-transfer': handleInvite('transfer'),
			'transfer-accepted': handleAccepted('transfer'),
			'transfer-call-rejected': handleRejected('transfer'),
			'transfer-call-failed': handleFailed('transfer'),

			'incoming-conference-invite': handleInvite('conference'),
			'conference-accepted': handleAccepted('conference'),
			'conference-call-rejected': handleRejected('conference'),
			'conference-call-failed': handleFailed('conference')
		};

		Object.entries(listeners).forEach(([event, handler]) => socket.on(event, handler));

		return () => {
			Object.entries(listeners).forEach(([event, handler]) => socket.off(event, handler));
		};
	}, [socket, agentPermissions, activeCallId]);

	// =================== Call Functions ===================
	async function respondCall(choice, callId) {
		const call = incomingCalls.find(c => c.id === callId);
		if (!call) return;
		const incomingCallId = callId;

		if (choice === 'accept') {
			let canCall =
				localStorage.getItem('activeCallId') == null && localStorage.getItem('activeOutgoingCallId') == null;
			if (!canCall) {
				setActiveCallId(localStorage.getItem('activeCallId'));
				setActiveOutgoingCallId(localStorage.getItem('activeOutgoingCallId'));
				return;
			}

			stopRingtone();
			setHiddenCalls(prev => prev.filter(id => id !== callId));

			await establishCallLeg(callId, sdp =>
				socket.emit('browser-offer', {
					clientId,
					userId,
					callId: incomingCallId,
					sdp
				})
			);
		}

		if (choice === 'reject') {
			// FIXED: Consistent ringtone stopping
			stopRingtone();

			socket.emit('reject-call', { clientId, userId, callId });

			// Remove from incomingCalls
			setIncomingCalls(prev => {
				const updated = prev.filter(c => c.id !== callId);
				localStorage.setItem('incomingCalls', JSON.stringify(updated));
				return updated;
			});

			// Remove from hiddenCalls too
			setHiddenCalls(prev => prev.filter(id => id !== callId));

			// Stop call timer
			stopCallTimer(callId);

			// Reset active call if this was active
			if (activeCallId === callId) {
				setActiveCallId(null);
				localStorage.removeItem('activeCallId');
			}
		}
	}

	// Everything involved in dropping our own side of a call. Shared by
	// terminateCall (which also tells the server to end the call for everyone)
	// and leaveCall (which does not).
	const teardownCallLeg = callId => {
		dropOfflineCall(callId);

		// Tell a handshake still waiting on the mic prompt to give the device
		// back when it resolves - it has not registered anything we could stop.
		const establishing = establishingLegsRef.current[callId];
		if (establishing) {
			establishing.cancelled = true;
			delete establishingLegsRef.current[callId];
		}

		stopRecording(callId);
		stopRingtone();

		setIncomingCalls(prev => {
			const updated = prev.filter(c => c.id !== callId);
			localStorage.setItem('incomingCalls', JSON.stringify(updated));
			return updated;
		});

		setHiddenCalls(prev => prev.filter(id => id !== callId));
		cleanupCallAudio(callId);
		releaseLocalStream(callId);

		if (pcRefs.current[callId]) {
			pcRefs.current[callId].close();
			delete pcRefs.current[callId];
		}
		delete pendingRemoteCandidatesRef.current[callId];
		stopMonitoringCallMedia(callId);
		stopCallTimer(callId);
		setConferenceCallIds(prev => prev.filter(id => id !== callId));
		clearTransferResponseTimer(callId);
		setTransferState(callId, null);

		// FIXED: Reset activeCallId when terminating the active call
		if (activeCallId == callId) {
			setActiveCallId(null);
			localStorage.removeItem('activeCallId');
		}
	};

	const terminateCall = callId => {
		socket.emit('terminate-call', { clientId: clientId, userId: userId, callId: callId });
		teardownCallLeg(callId);
	};

	// Hang up our own leg while the call carries on for whoever else is on it:
	// what agent A does once B has taken the transfer, and what any agent does
	// to step out of a conference. terminateCall is the opposite - it ends the
	// call for everyone.
	const leaveCall = callId => {
		socket.emit('leave-call', { clientId, userId, callId });
		teardownCallLeg(callId);
	};

	// =================== Call Transfer Functions ===================

	const markTransferPending = (callId, mode = 'transfer') => {
		transferPendingCallIdRef.current = callId;
		transferModeRef.current = mode;
		setTransferPendingCallId(callId);
	};

	// Agent A invites agent B onto the call. Nothing changes on this side yet:
	// the call stays up until B actually accepts, so a declined or failed
	// request leaves the customer exactly where they were.
	//
	// mode decides what happens once B is in - 'transfer' hands the call over
	// and drops our leg, 'conference' keeps us on it alongside B. Both use the
	// same server exchange; only our own reaction to transfer-accepted differs.
	const requestCallTransfer = (callId, targetUserId, mode = 'transfer') => {
		if (!callId || !targetUserId) return;
		if (mode === 'conference' ? !canAddToConference : !canTransferCalls) return;

		// A card restored from localStorage after a reload outlives the call it
		// describes, and transferring one of those is what "Call not found or
		// already ended" looks like from the server. The peer connection is the
		// honest signal that this leg is still up.
		const pc = pcRefs.current[callId];
		if (!pc || pc.connectionState === 'closed' || pc.signalingState === 'closed') {
			handleShowSnackbar('That call is no longer active', 'error');
			return;
		}

		// userId is not in the documented payload, but every other call-scoped
		// event here carries it and the server has to know who is handing the
		// call over to fill in fromUserId on the other side.
		const payload = { clientId, userId, callId, targetUserId };
		const event = mode === 'conference' ? 'conference-call-request' : 'transfer-call-request';
		console.info(`[${mode}] requesting`, payload);
		socket.emit(event, payload);
		markTransferPending(callId, mode);

		// Hold the call and show progress until the target answers. We used to
		// leave straight away because the old contract never told the
		// requesting agent the outcome; now that transfer-accepted and
		// transfer-call-rejected are distinct events, waiting is what makes
		// "transferring / accepted / declined" showable - and it stops a
		// declined transfer from leaving the customer with nobody.
		const targetName = agentDirectory.find(a => String(a.id) === String(targetUserId))?.username;
		setTransferState(callId, {
			mode,
			state: 'pending',
			message: targetName
				? `${mode === 'conference' ? 'Inviting' : 'Transferring to'} ${targetName}...`
				: `${mode === 'conference' ? 'Inviting agent' : 'Transferring'}...`
		});

		// Nothing in the contract guarantees a reply, so do not let the card sit
		// on "Transferring..." for the rest of the call if one never comes.
		transferResponseTimers.current[callId] = setTimeout(() => {
			delete transferResponseTimers.current[callId];
			if (transferPendingCallIdRef.current !== callId) return;

			markTransferPending(null);
			setTransferState(callId, { mode, state: 'failed', message: 'No response - still on the call' }, 6000);
			handleShowSnackbar('No response from the other agent', 'warning');
		}, TRANSFER_RESPONSE_TIMEOUT_MS);
	};

	// Same request, but we stay on the call once the other agent joins.
	const addAgentToCall = (callId, targetUserId) => requestCallTransfer(callId, targetUserId, 'conference');

	// Agent B picks the transferred leg up. The handshake is identical to
	// answering a fresh call, so the same offer goes out - just on
	// transfer-call-accept instead of browser-offer.
	const acceptCallTransfer = async callId => {
		const transfer = incomingTransfers.find(t => t.callId === callId);
		if (!transfer) return;

		const canCall =
			localStorage.getItem('activeCallId') == null && localStorage.getItem('activeOutgoingCallId') == null;
		if (!canCall) {
			handleShowSnackbar('Finish your current call before accepting a transfer', 'warning');
			return;
		}

		stopRingtone();
		setIncomingTransfers(prev => prev.filter(t => t.callId !== callId));

		// We are JOINING this call, so nothing about it is still pending on our
		// side. Without this, transferring a call away and later being given it
		// back leaves our old pending marker on the same callId - the
		// transfer-accepted for the return trip then matches it and hangs us up
		// on the call we just picked up.
		if (transferPendingCallIdRef.current === callId) {
			markTransferPending(null);
		}
		clearTransferResponseTimer(callId);
		setTransferState(callId, null);

		// The active-call card renders off incomingCalls, and a transferred
		// call has never been in that list on this side.
		setIncomingCalls(prev => {
			if (prev.some(c => c.id === callId)) return prev;
			const updated = [
				...prev,
				{
					id: callId,
					name: transfer.callerName,
					number: transfer.callerNumber,
					timer: 0,
					// Kept on the call so the active card can still say where it
					// came from - once the invite card is gone this is the only
					// record that it was handed over rather than dialled in.
					fromUserId: transfer.fromUserId,
					joinedVia: transfer.mode
				}
			];
			localStorage.setItem('incomingCalls', JSON.stringify(updated));
			return updated;
		});

		// A conference is exactly what we are joining, so the card should offer
		// Leave rather than only End Call, which would hang up on everyone.
		if (transfer.mode === 'conference') {
			setConferenceCallIds(prev => (prev.includes(callId) ? prev : [...prev, callId]));
		}

		// Answer on the same family the invite arrived on. A conference accept
		// deliberately omits fromUserId - the documented payload has no such
		// field, unlike its transfer counterpart.
		await establishCallLeg(callId, sdp => {
			if (transfer.mode === 'conference') {
				socket.emit('conference-call-accept', { clientId, userId, callId, sdp });
			} else {
				socket.emit('transfer-call-accept', {
					clientId,
					userId,
					callId,
					sdp,
					fromUserId: transfer.fromUserId
				});
			}
		});
	};

	const rejectCallTransfer = callId => {
		const transfer = incomingTransfers.find(t => t.callId === callId);
		if (!transfer) return;

		setIncomingTransfers(prev => prev.filter(t => t.callId !== callId));

		const event = transfer.mode === 'conference' ? 'conference-call-reject' : 'transfer-call-reject';
		socket.emit(event, { clientId, userId, callId, fromUserId: transfer.fromUserId });
	};

	function startCallTimer(callId) {
		if (timerRefs.current[callId]) clearInterval(timerRefs.current[callId]);

		// First set timer to 0 AND THEN start interval after state has updated
		setIncomingCalls(prev => prev.map(call => (call.id === callId ? { ...call, timer: 0 } : call)));
		timerRefs.current[callId] = setInterval(() => {
			setIncomingCalls(prev =>
				prev.map(call => (call.id === callId ? { ...call, timer: (call.timer || 0) + 1 } : call))
			);
		}, 1000);
	}

	function stopCallTimer(callId) {
		if (timerRefs.current[callId]) {
			clearInterval(timerRefs.current[callId]);
			delete timerRefs.current[callId];
		}
		setIncomingCalls(prev => prev.map(call => (call.id === callId ? { ...call, timer: 0 } : call)));
	}

	const value = useMemo(
		() => ({
			// =================== States ===================
			enableCallRingtone,
			setEnableCallRingtone,
			isRingtonePlaying,
			setIsRingtonePlaying,
			incomingCalls,
			setIncomingCalls,
			activeCallId,
			setActiveCallId,
			hiddenCalls,
			setHiddenCalls,
			callTimers,
			setCallTimers,
			activeReplyCallId,
			setActiveReplyCallId,
			showHiddenDropdown,
			setShowHiddenDropdown,
			cardOffsets,
			setCardOffsets,
			cardPositions,
			setCardPositions,
			snackbar,
			setSnackbar,
			selectedRecipient,
			setselectedRecipient,
			openSessionModal,
			setOpenSessionModal,
			openn,
			setOpenn,

			// =================== Outgoing Call States ===================
			outgoingCalls,
			setOutgoingCalls,
			outgoingCardPositions,
			setOutgoingCardPositions,
			activeOutgoingCallId,
			setActiveOutgoingCallId,

			// =================== Refs ===================
			timerRefs,
			pcRefs,
			ringtoneRef,
			dropdownRef,
			quickRepliesRef,
			callRefs,
			outgoingPcRefs,
			outgoingStreamRefs,
			outgoingTimerRefs,

			// =================== LocalStorage Data ===================
			clientId,
			userId,
			agentPermissions,

			// =================== Handlers ===================
			handleCallRingtone,
			toggleMinimizeAll,
			toggleMute,
			hideCall,
			restoreCalls,
			toggleReplies,
			handleDragEnd,
			handleRestoreCall,
			sendQuickReplyMessage,
			handleShowSnackbar,
			handleQuickReplySelect,

			// =================== Call Functions ===================
			respondCall,
			terminateCall,
			leaveCall,
			startCallTimer,
			stopCallTimer,

			// =================== Call Transfer / Conference ===================
			canTransferCalls,
			canAddToConference,
			conferenceCallIds,
			agentsOnCall,
			refreshAgentsOnCall,
			transferStatus,
			agentDirectory,
			loadAgentDirectory,
			incomingTransfers,
			setIncomingTransfers,
			transferPendingCallId,
			requestCallTransfer,
			addAgentToCall,
			acceptCallTransfer,
			rejectCallTransfer,

			// =================== Outgoing Call Functions ===================
			initiateOutgoingCall,
			terminateOutgoingCall,
			startOutgoingCallTimer,
			stopOutgoingCallTimer,
			// =================== Recording Functions ===================
			startRecording,
			stopRecording
		}),
		[
			enableCallRingtone,
			isRingtonePlaying,
			incomingCalls,
			activeCallId,
			hiddenCalls,
			callTimers,
			activeReplyCallId,
			showHiddenDropdown,
			cardOffsets,
			cardPositions,
			snackbar,
			incomingTransfers,
			transferPendingCallId,
			conferenceCallIds,
			agentsOnCall,
			transferStatus,
			agentDirectory,
			outgoingCalls,
			outgoingCardPositions,
			activeOutgoingCallId
		]
	);

	return <CallState.Provider value={value}>{children}</CallState.Provider>;
}

export default CallContext;
